import { Link } from "react-router-dom";
import { Show, SignIn } from "@clerk/react";

import { useAuth } from "../lib/auth.js";
import { AUTH_REDIRECT_URL, clerkAppearance } from "../lib/clerk-appearance.js";
import { AuthCard, AuthProblem, AuthShell, AuthSkeleton, AuthWidgetSlot } from "./AuthShell.js";

/**
 * The sign-in page.
 *
 * Clerk renders the form; this page supplies the frame around it. There is no
 * local email/password state and no POST to /api/auth/login — that endpoint no
 * longer exists, and re-adding a password field here would only produce a form
 * the server cannot accept.
 *
 * `/login` is a splat route because Clerk's path routing uses sub-paths for the
 * second factor, SSO callback and password reset. `routing="path"` keeps those
 * steps on this origin instead of bouncing to Clerk's Account Portal, which is
 * what the sign-up button used to do before there was a /sign-up page.
 */
export function Login() {
  const { host, hostError, loading, clerkUnavailable, refresh, signOut } = useAuth();

  // Clerk's script never arrived. The sign-in form cannot render, and a blank
  // card is indistinguishable from a broken page, so say what happened and
  // offer the one action that can help.
  if (clerkUnavailable) {
    return (
      <AuthShell>
        <AuthProblem
          eyebrow="Sign-in unavailable"
          title="Could not reach sign-in"
          message="The sign-in service could not be loaded. That is usually the network rather than you — a guest wifi portal, a proxy, or a content blocker that stops scripts."
          onRetry={() => window.location.reload()}
        />
      </AuthShell>
    );
  }

  // Clerk signed the user in, but the Worker could not answer. Showing the Clerk
  // form again would invite a second sign-in that cannot fix a server fault, so
  // say what happened and offer the one action that can.
  if (hostError) {
    return (
      <AuthShell>
        <AuthProblem
          eyebrow="Account unavailable"
          title="We could not load your account"
          message={hostError}
          onRetry={() => void refresh()}
        />
      </AuthShell>
    );
  }

  // Signed in and the Worker recognises the host: send them on rather than
  // showing a second sign-in form.
  if (host) {
    return (
      <AuthShell>
        <div className="card auth-card" style={{ textAlign: "center" }}>
          <p className="eyebrow">You are signed in</p>
          <h1>Welcome back</h1>
          <p className="muted">{host.email}</p>
          <Link className="button" to="/dashboard">
            Go to dashboard
          </Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <AuthCard>
        <p className="eyebrow">Host sign-in</p>
        <h1>Welcome back</h1>
        <p className="muted">
          Hosts sign in. Guests never need an account — they scan the QR code and upload.
        </p>

        {/* `Show` is the current @clerk/react API. <SignedIn>/<SignedOut> were
            removed in Core 3 — see the version note in main.tsx. The skeleton
            covers the gap before Clerk's script has mounted the widget. */}
        {loading ? (
          <AuthSkeleton label="Loading secure sign-in…" />
        ) : (
          <>
            <Show when="signed-in">
              <p className="muted">
                You are signed in with Clerk, but this account has no host profile yet. That is
                usually a verification step that has not finished — check your email, then try
                again. If it still does not work, sign out and back in.
              </p>
              <div className="stack" style={{ gap: "0.75rem" }}>
                <button className="button" type="button" onClick={() => void refresh()}>
                  Try again
                </button>
                {/* A dead end is the one thing this state must not be: if the
                    Worker keeps refusing the session, signing out is the only
                    way back to a form that accepts a different account. */}
                <button
                  className="button button-secondary"
                  type="button"
                  onClick={() => void signOut()}
                >
                  Sign out
                </button>
              </div>
            </Show>

            <Show when="signed-out">
              <AuthWidgetSlot label="Loading secure sign-in…">
                <SignIn
                  routing="path"
                  path="/login"
                  signUpUrl="/sign-up"
                  fallbackRedirectUrl={AUTH_REDIRECT_URL}
                  appearance={clerkAppearance}
                />
              </AuthWidgetSlot>
              <div style={{ marginTop: "1.25rem", textAlign: "center", fontSize: "0.88rem" }}>
                <span className="muted">First-time event host? </span>
                <Link to="/sign-up" style={{ color: "var(--accent)", fontWeight: 600 }}>
                  Create your account &rarr;
                </Link>
              </div>
            </Show>
          </>
        )}
      </AuthCard>
    </AuthShell>
  );
}
